import { Header } from "@/components/Header";
import { StaticRouteBreadcrumbs } from "@/components/StaticRouteBreadcrumbs";
import { Footer } from "@/components/Footer";
import { AnnouncementBar } from "@/components/AnnouncementBar";

import { Helmet } from "react-helmet-async";
import { Truck, Package, RotateCcw, Star } from "lucide-react";
import brandStory from "@/assets/brand-story.webp";
import designServices from "@/assets/design-services.webp";
import { FREE_SHIPPING_THRESHOLD, DELIVERY_WINDOW_COPY, SHIPPING_REGION_COPY } from "@/lib/shipping";

import { routeHead } from "@/lib/routeHead";
import { CATALOGUE_IN_STOCK } from "@/data/categoryStock";

// Title/description come from scripts/routes.mjs, like the prerendered head.
const head = routeHead("/about");

// Only facts a visitor can check: catalogue size and published policies.
// No headcount, no founding date, no premises, no staff bios — LoftLoom is a
// storefront, and inventing people to fill this page is not an option.
const stats = [
  // Measured at build time; a hard-coded figure went stale as the supplier's range shrank.
  { icon: Package, value: `${(Math.floor(CATALOGUE_IN_STOCK / 100) * 100).toLocaleString("en-US")}+`, label: "Pieces in stock" },
  { icon: RotateCcw, value: "30-Day", label: "Returns" },
  { icon: Truck, value: `$${FREE_SHIPPING_THRESHOLD}+`, label: `Free shipping over $${FREE_SHIPPING_THRESHOLD}` },
  { icon: Star, value: "Free", label: "Room plan, on request" },
];

const About = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{head.title}</title>
        <meta name="description" content={head.description} />
        <link rel="canonical" href="https://loftloom.com/about" />
        <meta property="og:title" content={head.title} />
        <meta property="og:description" content={head.description} />
        <meta property="og:url" content="https://loftloom.com/about" />
        <meta property="og:type" content="website" />
      </Helmet>
      <AnnouncementBar />
      <Header />
      <StaticRouteBreadcrumbs />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
          <img src={brandStory} alt="A modern living room with a low sofa and warm wood tones" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-foreground/40 flex items-center justify-center">
            <h1 className="font-display text-4xl md:text-6xl font-semibold text-background tracking-tight">About LoftLoom</h1>
          </div>
        </section>

        {/* By the Numbers */}
        <section id="stats" className="py-10 md:py-14 bg-secondary/40 border-b border-border scroll-mt-24">
          <div className="max-w-4xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
                    <stat.icon className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-foreground tabular-nums">{stat.value}</p>
                  <p className="editorial-label mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="max-w-3xl mx-auto px-4 py-16 md:py-24 space-y-12">
          <section className="space-y-4">
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">What LoftLoom is</h2>
            <p className="text-muted-foreground leading-relaxed">
              LoftLoom is a curated online storefront. We don't manufacture furniture and we don't run a
              warehouse or a showroom. What we do is read through a very large catalogue of modern furniture,
              lighting and decor, and put forward the pieces worth your attention — organised by room, by
              material and by how a space actually gets used.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              When you place an order, it is fulfilled and shipped by our supply partner, who holds the stock.
              Payment is completed on their checkout, which is why their name — not ours — appears on your bank
              statement. We tell you that before you pay rather than after.
            </p>
          </section>

          <section id="approach" className="space-y-4 scroll-mt-24">
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">How we work</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">Curation, not invention</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Every piece here comes from a supplier catalogue. We select and describe, and where the
                  supplier's own data is unreliable — measurements, materials — we leave it out rather than
                  guess on your behalf.
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">Plain shipping terms</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Free shipping over ${FREE_SHIPPING_THRESHOLD}. {DELIVERY_WINDOW_COPY} {SHIPPING_REGION_COPY}
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">Returns you can read</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  30 days to return an item. The returns page states exactly when return shipping is on us and
                  when it's on you — no softening language.
                </p>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">No manufactured urgency</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  No countdowns, no invented stock levels, no reviews we wrote ourselves, and no former price
                  we can't substantiate. If it isn't verifiable, it isn't on the page.
                </p>
              </div>
            </div>
          </section>

          <section className="aspect-video overflow-hidden">
            <img src={designServices} alt="A dining space arranged with a wood table and upholstered chairs" className="w-full h-full object-cover" />
          </section>
        </div>

        <div className="max-w-3xl mx-auto px-4 pb-16 md:pb-20">
          <section className="space-y-4">
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">Questions</h2>
            <p className="text-muted-foreground leading-relaxed">
              Anything about an order, a piece, or a measurement — email{" "}
              <a href="mailto:hello@loftloom.com" className="text-primary underline">hello@loftloom.com</a>{" "}
              or use the contact page. A person answers.
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;
